import { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import unescapedText from 'I18n/utils/unescapedText';
import { Panel, PanelSection } from 'foundations-components/transitional/components/Panel';
import { PanelBody } from 'foundations-components/transitional/components/PanelBody';
import { Button } from 'foundations-components/transitional/components/Button';
import { CloseButton } from 'foundations-components/transitional/components/CloseButton';
import { H4 } from 'foundations-components/transitional/components/H4';
import { H5 } from 'foundations-components/transitional/components/H5';
import { Paragraph } from 'foundations-components/transitional/components/Paragraph';
import { FileButton } from 'foundations-components/transitional/components/FileButton';
import { Image } from 'foundations-components/transitional/components/Image';
import { InsertImageIcon } from 'foundations-assets/icons/components/InsertImageIcon';
import { LockedIcon } from 'foundations-assets/icons/components/LockedIcon';
import { RightIcon } from 'foundations-assets/icons/components/RightIcon';
import { H7 } from 'foundations-components/transitional/components/H7';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import { View } from 'foundations-components/transitional/components/View';
import { HR } from 'foundations-components/transitional/components/HR';
import { Flex } from 'foundations-components/components/Flex';
import { Fieldset } from 'foundations-components/transitional/components/Fieldset';
import { useIsUngated } from '../hooks/useIsUngated';
import { PasswordResetPanel } from './PasswordResetPanel';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const PANEL_WIDTH_BY_BREAKPOINT = {
  phone: '100%',
  tablet: '80%',
  desktop: 600
};
const PROFILE_PHOTO_UPLOAD_GATE = 'PortalsPlatform::Settings::ProfilePhotoUpload';
const RESET_PASSWORD_GATE = 'PortalsPlatform::Settings::ResetPassword';
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;
const ALLOWED_MIME_TYPES = ['image/png', 'image/jpeg'];
function ProfilePhotoSection({
  loginVerifyData
}) {
  const [localAvatarUrl, setLocalAvatarUrl] = useState();
  const [imageFailed, setImageFailed] = useState(false);
  const [errorMessage, setErrorMessage] = useState();
  const localAvatarUrlRef = useRef(localAvatarUrl);
  localAvatarUrlRef.current = localAvatarUrl;
  const displayUrl = localAvatarUrl !== null && localAvatarUrl !== void 0 ? localAvatarUrl : loginVerifyData.avatarUrl;
  useEffect(() => {
    return () => {
      if (localAvatarUrlRef.current) {
        URL.revokeObjectURL(localAvatarUrlRef.current);
      }
    };
  }, []);
  const showImage = Boolean(displayUrl) && !imageFailed;
  const initials = useMemo(() => {
    const firstName = loginVerifyData.firstname || '';
    const lastName = loginVerifyData.lastname || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'U';
  }, [loginVerifyData]);
  const handleImageError = useCallback(() => {
    setImageFailed(true);
    if (localAvatarUrl) {
      URL.revokeObjectURL(localAvatarUrl);
      setLocalAvatarUrl(undefined);
      setErrorMessage(unescapedText('app.myAccountPopover.settings.profilePhoto.errorUploadFailed'));
    }
  }, [localAvatarUrl]);
  const handleFileChange = useCallback(event => {
    var _event$target$files;
    const file = (_event$target$files = event.target.files) === null || _event$target$files === void 0 ? void 0 : _event$target$files[0];
    event.target.value = '';
    if (!file) {
      return;
    }
    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
      setErrorMessage(unescapedText('app.myAccountPopover.settings.profilePhoto.errorInvalidType'));
      return;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setErrorMessage(unescapedText('app.myAccountPopover.settings.profilePhoto.errorFileTooLarge'));
      return;
    }
    setErrorMessage(undefined);
    setImageFailed(false);
    if (localAvatarUrl) {
      URL.revokeObjectURL(localAvatarUrl);
    }
    setLocalAvatarUrl(URL.createObjectURL(file));
  }, [localAvatarUrl]);
  return /*#__PURE__*/_jsxs(Flex, {
    alignItems: "center",
    gap: "400",
    marginBlock: "300",
    children: [/*#__PURE__*/_jsxs(Flex, {
      position: "relative",
      inlineSize: "64px",
      blockSize: "64px",
      children: [showImage ? /*#__PURE__*/_jsx(Image, {
        src: displayUrl,
        alt: "Profile",
        shape: "circle",
        responsive: false,
        inlineSize: "64px",
        blockSize: "64px",
        objectFit: "cover",
        bordered: true,
        borderColor: "border-core-default",
        onError: handleImageError
      }) : /*#__PURE__*/_jsx(Flex, {
        backgroundColor: "fill-primary-pressed",
        borderRadius: "full",
        borderWidth: "100",
        borderStyle: "solid",
        borderColor: "border-core-default",
        inlineSize: "64px",
        blockSize: "64px",
        alignItems: "center",
        justifyContent: "center",
        children: /*#__PURE__*/_jsx(Paragraph, {
          color: "text-inverse-default",
          marginBlock: "0",
          typography: "heading-200",
          children: initials
        })
      }), /*#__PURE__*/_jsx(Flex, {
        position: "absolute",
        insetInlineEnd: "0",
        insetBlockEnd: "0",
        children: /*#__PURE__*/_jsx(FileButton, {
          use: "unstyled",
          paddingInline: "0",
          accept: ['image/png', 'image/jpeg'],
          onChange: handleFileChange,
          "aria-label": unescapedText('app.myAccountPopover.settings.profilePhoto.uploadButton'),
          children: /*#__PURE__*/_jsx(Flex, {
            inlineSize: "24px",
            blockSize: "24px",
            borderRadius: "full",
            backgroundColor: "fill-primary-default",
            alignItems: "center",
            justifyContent: "center",
            children: /*#__PURE__*/_jsx(InsertImageIcon, {
              size: "100",
              color: "text-primary-default"
            })
          })
        })
      })]
    }), /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      gap: "50",
      alignItems: "start",
      children: [/*#__PURE__*/_jsx(Paragraph, {
        color: "text-core-default",
        marginBlock: "0",
        typography: "body-100",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "app.myAccountPopover.settings.profilePhoto.title"
        })
      }), /*#__PURE__*/_jsx(Paragraph, {
        color: "text-core-default",
        marginBlock: "0",
        typography: "detail-200",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "app.myAccountPopover.settings.profilePhoto.description"
        })
      }), /*#__PURE__*/_jsx(FileButton, {
        use: "secondary-ghost",
        size: "small",
        accept: ['image/png', 'image/jpeg'],
        onChange: handleFileChange,
        children: /*#__PURE__*/_jsx(Paragraph, {
          color: "text-core-default",
          marginBlock: "0",
          typography: "detail-200",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "app.myAccountPopover.settings.profilePhoto.uploadButton"
          })
        })
      }), errorMessage && /*#__PURE__*/_jsx(Paragraph, {
        color: "text-core-default",
        marginBlock: "0",
        typography: "detail-200",
        children: errorMessage
      })]
    })]
  });
}
function SettingsForm({
  loginVerifyData,
  onOpenChangePassword
}) {
  const EMPTY_PLACEHOLDER = '\u2014';
  const fullName = [loginVerifyData.firstname, loginVerifyData.lastname].filter(Boolean).join(' ') || EMPTY_PLACEHOLDER;
  const jobTitle = loginVerifyData.jobTitle || EMPTY_PLACEHOLDER;
  const phone = loginVerifyData.phone || EMPTY_PLACEHOLDER;
  const email = loginVerifyData.email || EMPTY_PLACEHOLDER;
  const company = loginVerifyData.company || EMPTY_PLACEHOLDER;
  const isProfilePhotoUploadEnabled = useIsUngated(PROFILE_PHOTO_UPLOAD_GATE);
  const isResetPasswordEnabled = useIsUngated(RESET_PASSWORD_GATE);
  return /*#__PURE__*/_jsxs(View, {
    children: [isProfilePhotoUploadEnabled && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(ProfilePhotoSection, {
        loginVerifyData: loginVerifyData
      }), /*#__PURE__*/_jsx(HR, {
        distance: "flush"
      })]
    }), /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      marginBlockEnd: "300",
      children: [/*#__PURE__*/_jsx(H5, {
        color: "text-core-default",
        marginBlockStart: "300",
        marginBlockEnd: "200",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "app.myAccountPopover.settings.account.title"
        })
      }), /*#__PURE__*/_jsx(View, {
        marginBlockEnd: "300",
        children: /*#__PURE__*/_jsx(Fieldset, {
          _size: "small",
          children: /*#__PURE__*/_jsx(FormControl, {
            label: unescapedText('app.myAccountPopover.settings.account.membershipEmailLabel'),
            help: unescapedText('app.myAccountPopover.settings.account.membershipEmailTooltip'),
            children: /*#__PURE__*/_jsx(TextInput, {
              name: "membershipEmail",
              value: email,
              typography: "body-100",
              readOnly: true,
              disabled: true
            })
          })
        })
      }), /*#__PURE__*/_jsx(View, {
        marginBlockEnd: "300",
        children: /*#__PURE__*/_jsx(Fieldset, {
          _size: "small",
          children: /*#__PURE__*/_jsx(FormControl, {
            label: unescapedText('app.myAccountPopover.settings.account.companyLabel'),
            children: /*#__PURE__*/_jsx(TextInput, {
              name: "company",
              value: company,
              typography: "body-100",
              readOnly: true,
              disabled: true
            })
          })
        })
      })]
    }), /*#__PURE__*/_jsx(HR, {
      distance: "flush"
    }), /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      marginBlockStart: "300",
      marginBlockEnd: "300",
      children: [/*#__PURE__*/_jsx(H5, {
        color: "text-core-default",
        marginBlockEnd: "200",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "app.myAccountPopover.settings.profile.title"
        })
      }), /*#__PURE__*/_jsx(View, {
        marginBlockEnd: "200",
        flexGrow: 1,
        minInlineSize: "0",
        children: /*#__PURE__*/_jsx(Fieldset, {
          _size: "small",
          children: /*#__PURE__*/_jsx(FormControl, {
            label: unescapedText('app.myAccountPopover.settings.profile.fullNameLabel'),
            children: /*#__PURE__*/_jsx(TextInput, {
              name: "fullName",
              value: fullName,
              typography: "body-100",
              readOnly: true,
              disabled: true
            })
          })
        })
      }), /*#__PURE__*/_jsx(View, {
        marginBlockEnd: "200",
        flexGrow: 1,
        minInlineSize: "0",
        children: /*#__PURE__*/_jsx(Fieldset, {
          _size: "small",
          children: /*#__PURE__*/_jsx(FormControl, {
            label: unescapedText('app.myAccountPopover.settings.profile.jobTitleLabel'),
            children: /*#__PURE__*/_jsx(TextInput, {
              name: "jobTitle",
              value: jobTitle,
              typography: "body-100",
              readOnly: true,
              disabled: true
            })
          })
        })
      }), /*#__PURE__*/_jsx(View, {
        marginBlockEnd: "200",
        children: /*#__PURE__*/_jsx(Fieldset, {
          _size: "small",
          children: /*#__PURE__*/_jsx(FormControl, {
            label: unescapedText('app.myAccountPopover.settings.profile.phoneLabel'),
            children: /*#__PURE__*/_jsx(TextInput, {
              name: "phone",
              value: phone,
              typography: "body-100",
              readOnly: true,
              disabled: true
            })
          })
        })
      })]
    }), /*#__PURE__*/_jsx(HR, {
      distance: "flush"
    }), isResetPasswordEnabled && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        marginBlockStart: "300",
        marginBlockEnd: "300",
        children: [/*#__PURE__*/_jsx(H5, {
          color: "text-core-default",
          marginBlockEnd: "200",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "app.myAccountPopover.settings.security.title"
          })
        }), /*#__PURE__*/_jsx(View, {
          children: /*#__PURE__*/_jsx(Fieldset, {
            _size: "small",
            children: /*#__PURE__*/_jsx(FormControl, {
              label: unescapedText('app.myAccountPopover.settings.security.changePasswordLabel'),
              children: /*#__PURE__*/_jsx(Flex, {
                inlineSize: "100%",
                children: /*#__PURE__*/_jsx(Button, {
                  use: "unstyled",
                  onClick: onOpenChangePassword,
                  flex: "auto",
                  children: /*#__PURE__*/_jsx(View, {
                    inlineSize: "100%",
                    borderWidth: "100",
                    borderStyle: "solid",
                    borderColor: "border-core-default",
                    borderRadius: "200",
                    backgroundColor: "fill-surface-default",
                    paddingInline: "200",
                    paddingBlock: "100",
                    children: /*#__PURE__*/_jsxs(Flex, {
                      alignItems: "center",
                      justifyContent: "space-between",
                      inlineSize: "100%",
                      gap: "200",
                      children: [/*#__PURE__*/_jsxs(Flex, {
                        alignItems: "center",
                        gap: "200",
                        children: [/*#__PURE__*/_jsx(LockedIcon, {
                          size: "300",
                          color: "text-core-default"
                        }), /*#__PURE__*/_jsx(Paragraph, {
                          color: "text-core-default",
                          marginBlock: "0",
                          typography: "body-100",
                          children: /*#__PURE__*/_jsx(FormattedMessage, {
                            message: "app.myAccountPopover.settings.security.changePassword"
                          })
                        })]
                      }), /*#__PURE__*/_jsx(RightIcon, {
                        size: "300",
                        color: "text-core-default"
                      })]
                    })
                  })
                })
              })
            })
          })
        })]
      }), /*#__PURE__*/_jsx(HR, {
        distance: "flush"
      })]
    })]
  });
}
export function SettingsPanel({
  onClose,
  loginVerifyData
}) {
  const breakpoint = useBreakpoint();
  const [showChangePassword, setShowChangePassword] = useState(false);
  const isPhone = breakpoint === 'phone';
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Panel, {
      width: PANEL_WIDTH_BY_BREAKPOINT[breakpoint],
      children: isPhone ? /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsxs(Flex, {
          position: "sticky",
          insetBlockStart: "0",
          zIndex: "1",
          backgroundColor: "fill-surface-default",
          borderBlockEndWidth: "100",
          borderBlockEndStyle: "solid",
          borderBlockEndColor: "border-core-default",
          paddingInline: "400",
          paddingBlock: "300",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "200",
          children: [/*#__PURE__*/_jsx(H4, {
            color: "text-core-default",
            marginBlock: "0",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "app.myAccountPopover.settings.heading"
            })
          }), /*#__PURE__*/_jsx(CloseButton, {
            inline: true,
            onClick: onClose,
            color: "text-core-default"
          })]
        }), /*#__PURE__*/_jsx(PanelBody, {
          children: /*#__PURE__*/_jsxs(PanelSection, {
            children: [/*#__PURE__*/_jsx(H7, {
              color: "text-core-default",
              marginBlockEnd: "300",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "app.myAccountPopover.settings.subtitle"
              })
            }), /*#__PURE__*/_jsx(HR, {
              distance: "flush"
            }), /*#__PURE__*/_jsx(SettingsForm, {
              loginVerifyData: loginVerifyData,
              onOpenChangePassword: () => setShowChangePassword(true)
            })]
          })
        })]
      }) : /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(CloseButton, {
          onClick: onClose,
          color: "text-core-default"
        }), /*#__PURE__*/_jsx(PanelBody, {
          children: /*#__PURE__*/_jsxs(PanelSection, {
            children: [/*#__PURE__*/_jsx(H4, {
              color: "text-core-default",
              marginBlockEnd: "200",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "app.myAccountPopover.settings.heading"
              })
            }), /*#__PURE__*/_jsx(H7, {
              color: "text-core-default",
              marginBlockEnd: "300",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "app.myAccountPopover.settings.subtitle"
              })
            }), /*#__PURE__*/_jsx(HR, {
              distance: "flush"
            }), /*#__PURE__*/_jsx(SettingsForm, {
              loginVerifyData: loginVerifyData,
              onOpenChangePassword: () => setShowChangePassword(true)
            })]
          })
        })]
      })
    }), showChangePassword && /*#__PURE__*/_jsx(PasswordResetPanel, {
      onClose: () => setShowChangePassword(false)
    })]
  });
}